import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { IActivity, KeyStage, InputMode } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { useStageTokens } from '../hooks/useStageTokens';
import { MultiModalInputArea } from './MultiModalInputArea';
import { useActivityInputModes } from '../hooks/useActivityInputModes';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

interface LongFormWritingActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const LongFormWritingActivity: React.FC<LongFormWritingActivityProps> = ({ activity, onComplete, keyStage }) => {
    const [text, setText] = useState('');
    const [activeMode, setActiveMode] = useState<InputMode>('keyboard');
    const { setStructuredData } = useStrokeStore.getState();
    const tokens = useStageTokens(keyStage);

    const prompt = activity.config?.prompt || 'Write your response here...';

    const flags = useFeatureFlags();
    const { allowedModes, defaultMode } = useActivityInputModes(activity);
    const multiModalEnabled = flags.inputModeSwitcherEnabled && allowedModes.length > 1;

    const canSubmit = text.trim().length > 0;

    const handleSubmit = () => {
        if (!canSubmit) return;
        setStructuredData({ text, inputModeUsed: activeMode });
        onComplete();
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                </View>
                <View style={styles.promptContainer}>
                    <Text style={styles.promptLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Prompt</Text>
                    <Text style={[styles.prompt, { fontSize: tokens.bodyFontSize + 2, lineHeight: tokens.bodyLineHeight + 2 }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{prompt}</Text>
                </View>
            </View>

            {multiModalEnabled ? (
                <View testID="long-form-input-area" style={styles.inputWrapper}>
                    <MultiModalInputArea
                        allowedModes={allowedModes}
                        defaultMode={defaultMode}
                        onChange={(val) => setText(val)}
                        onSubmit={(val, mode, meta) => {
                            if (!val.trim()) return;
                            setText(val);
                            if (meta?.voiceTranscript) {
                                useStrokeStore.getState().setStructuredData({
                                    voiceTranscript: meta.voiceTranscript,
                                    inputModeUsed: mode,
                                });
                            }
                            setActiveMode(mode);
                            onComplete();
                        }}
                        onModeChange={(mode) => setActiveMode(mode)}
                        placeholder="Type or speak your response..."
                    />
                </View>
            ) : (
                <View style={styles.inputWrapper}>
                    <TextInput
                        testID="long-form-input"
                        style={styles.textInput}
                        multiline
                        value={text}
                        onChangeText={setText}
                        placeholder="Start typing..."
                        placeholderTextColor="#94A3B8"
                        textAlignVertical="top"
                    />
                </View>
            )}

            <View style={styles.footer}>
                <TouchableOpacity
                    style={[styles.submitButton, !canSubmit && styles.disabledButton]}
                    onPress={handleSubmit}
                    disabled={!canSubmit}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your writing"
                    accessibilityState={{ disabled: !canSubmit }}
                >
                    <Text style={styles.submitButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>

            {/* Teacher Feedback Overlay (Read-Only Canvas) */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
    },
    header: {
        padding: 24,
        backgroundColor: '#F1F5F9',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#64748B',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 12,
    },
    promptContainer: {
        backgroundColor: '#FFFFFF',
        padding: 16,
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#3B82F6',
    },
    promptLabel: {
        fontSize: 12,
        fontWeight: '900',
        color: '#3B82F6',
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    prompt: {
        fontSize: 20,
        fontWeight: '600',
        color: '#1E293B',
        lineHeight: 28,
    },
    inputWrapper: {
        flex: 1,
        padding: 20,
        backgroundColor: '#F8FAFC',
    },
    textInput: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 16,
        fontSize: 18,
        lineHeight: 28,
        color: '#1E293B',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    footer: {
        padding: 20,
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
        alignItems: 'center',
    },
    submitButton: {
        backgroundColor: '#22c55e',
        paddingHorizontal: 32,
        paddingVertical: 18,
        borderRadius: 16,
        width: '100%',
        alignItems: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    disabledButton: {
        backgroundColor: '#CBD5E1',
        shadowOpacity: 0,
        elevation: 0,
    },
    submitButtonText: {
        color: '#FFFFFF',
        fontSize: 20,
        fontWeight: 'bold',
    },
});
